import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  validateCarryFactor,
  type CarryFactorResult,
} from "@/lovable-demo/lib/warranty/carry-factor";

export function CarryFactorDialog({
  current,
  label,
  onConfirm,
}: {
  current: number;
  label: string;
  onConfirm: (value: number, rationale: string) => Promise<CarryFactorResult>;
}) {
  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState(String(current * 100));
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reset = () => {
    setPercent(String(current * 100));
    setRationale("");
    setError(null);
  };
  const submit = async () => {
    const value = Number(percent) / 100;
    const invalid = validateCarryFactor(value, rationale);
    if (invalid) return setError(invalid);
    setBusy(true);
    setError(null);
    try {
      const result = await onConfirm(value, rationale);
      if (!result.ok) {
        setError(result.error);
        toast.error("Carry factor not changed", { description: result.error });
        return;
      }
      toast.success("Carry factor recorded and confirmed");
      setOpen(false);
      reset();
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs no-print"
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!busy) {
            setOpen(v);
            if (!v) reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              Quality Director authority and a rationale are required. Broadview records the value
              and audit entry together before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <label className="text-xs text-muted-foreground">
              Monthly carry factor (%)
              <Input
                className="num mt-1"
                type="number"
                min="0.1"
                max="100"
                step="0.1"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
              />
            </label>
            <label className="text-xs text-muted-foreground">
              Rationale (required)
              <Textarea
                className="mt-1 min-h-20"
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
              />
            </label>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={submit}>
              {busy ? "Recording…" : "Record change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
